import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFactoryStore } from "@/core/hooks/useFactory";
import { useLanguage } from "@/core/LanguageContext";
import { useAuth } from "@/core/AuthContext";
import { useUserFactories } from "@/core/hooks/useUserFactories";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  Activity,
  AlertCircle,
  ClipboardCheck,
  Cpu,
  Factory,
  Gauge,
  GitBranch,
  Layers,
  Loader2,
  RefreshCw,
  Settings,
} from "lucide-react";
import { Link } from "react-router-dom";
import { MetricChartDialog } from "@/core/components/exposed-values/MetricChartDialog";
import { ExposedValueGrid, ExposedValueTile } from "@/core/components/exposed-values/ExposedValueGrid";
import { ExposedValueMetricCard } from "@/core/components/exposed-values/ExposedValueMetricCard";
import { ExposedValueSettingsDialog } from "@/core/components/exposed-values/ExposedValueSettingsDialog";
import { ModuleStatsPopover } from "@/core/components/exposed-values/ModuleStatsPopover";
import { fetchCurrentValues } from "@/core/api/influxdb";
import { DatapointSelectorDialog, type DatapointSelection } from "@/core/components/DatapointSelectorDialog";
import { toast } from "sonner";
import type {
  Area,
  ExposedValueDefinition,
  ExposedValueMapping,
  FactoryInfo,
  FieldMapping,
  Line,
  LocationScope,
  RealTimeValue,
  Station,
} from "@/core/types/exposed-values";

const translationPrefix = "mod_performance.performance_management";

const overviewExposedValueNames = ["oee", "availability", "performance", "quality"];

const areaHighlightExposedValues = ["oee", "performance"];

const performanceExposedValueNameGroups = {
  oee: ["oee", "availability", "performance", "quality"],
  output: ["throughput", "cycle_time", "good_count", "total_count"],
  losses: ["scrap_rate", "downtime_minutes"],
};

const specialExposedValueNames = new Set(Object.values(performanceExposedValueNameGroups).flat());

const getLocationKey = (mapping: FieldMapping) => {
  if (mapping.station_id) return `station:${mapping.station_id}`;
  if (mapping.line_id) return `line:${mapping.line_id}`;
  if (mapping.area_id) return `area:${mapping.area_id}`;
  return `factory:${mapping.factory_id}`;
};

const getScopeKey = (scope: LocationScope) => `${scope.type}:${scope.id}`;

const PerformanceManagement = () => {
  const { selectedFactoryId } = useFactoryStore();
  const { t } = useLanguage();
  const { profile } = useAuth();
  const { data: userFactories = [] } = useUserFactories();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [realTimeValues, setRealTimeValues] = useState<Record<string, RealTimeValue>>({});
  const [isLoadingValues, setIsLoadingValues] = useState(false);
  const [chartDialogOpen, setChartDialogOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<ExposedValueMapping | null>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorContext, setSelectorContext] = useState<{
    scope: LocationScope;
    exposedValue: ExposedValueDefinition;
  } | null>(null);
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [focusedScopeKey, setFocusedScopeKey] = useState<string>("");
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { data: factoryInfo } = useQuery({
    queryKey: ["factory-details", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return null;
      const { data, error } = await supabase
        .schema("core")
        .from("factories")
        .select("id, tenant_id")
        .eq("id", selectedFactoryId)
        .maybeSingle();
      if (error) throw error;
      return data as FactoryInfo | null;
    },
    enabled: !!selectedFactoryId,
  });

  const { data: performanceModule } = useQuery({
    queryKey: ["core", "module-registry", "performance"],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema("core")
        .from("module_registry")
        .select("id, name, display_name")
        .eq("name", "performance")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: exposedValues = [], isLoading: loadingExposedValues } = useQuery({
    queryKey: ["core", "exposed-values", performanceModule?.id],
    queryFn: async () => {
      if (!performanceModule?.id) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("exposed_value_definitions")
        .select("id, name, display_name, description, module_id")
        .eq("module_id", performanceModule.id)
        .order("display_name");
      if (error) throw error;
      return data as ExposedValueDefinition[];
    },
    enabled: !!performanceModule?.id,
  });

  const {
    data: fieldMappings = [],
    isLoading: loadingMappings,
    refetch: refetchMappings,
  } = useQuery({
    queryKey: ["field-mappings", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("field_mappings")
        .select(
          `
          id,
          factory_id,
          area_id,
          line_id,
          station_id,
          datapoint:datapoints (
            field_name,
            measurement_name,
            display_name,
            bucket_name,
            unit:units (symbol)
          )
        `
        )
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true);
      if (error) throw error;
      const mappings = (data as FieldMapping[]) || [];
      return mappings.filter((mapping) => mapping.datapoint);
    },
    enabled: !!selectedFactoryId,
  });

  const fieldMappingIds = useMemo(() => fieldMappings.map((mapping) => mapping.id), [fieldMappings]);
  const exposedValueIds = useMemo(() => exposedValues.map((value) => value.id), [exposedValues]);

  const {
    data: exposedValueMappings = [],
    refetch: refetchExposedValueMappings,
  } = useQuery({
    queryKey: [
      "core",
      "exposed-value-mappings",
      selectedFactoryId,
      exposedValueIds.join(","),
      fieldMappingIds.join(","),
    ],
    queryFn: async () => {
      if (exposedValueIds.length === 0 || fieldMappingIds.length === 0 || !factoryInfo?.tenant_id) {
        return [];
      }
      const { data, error } = await supabase
        .schema("core")
        .from("datapoint_exposed_value_mapping")
        .select(
          `
          id,
          exposed_value_id,
          field_mapping_id,
          dynamic_key,
          exposed_value:exposed_value_definitions (
            id,
            name,
            display_name,
            description,
            module_id
          ),
          field_mapping:field_mappings (
            id,
            factory_id,
            area_id,
            line_id,
            station_id,
            datapoint:datapoints (
              display_name,
              field_name,
              measurement_name,
              bucket_name,
              unit:units (symbol)
            )
          )
        `
        )
        .eq("tenant_id", factoryInfo.tenant_id)
        .in("exposed_value_id", exposedValueIds)
        .in("field_mapping_id", fieldMappingIds);
      if (error) throw error;
      const mappings = (data as ExposedValueMapping[]) || [];
      return mappings.filter((mapping) => mapping.exposed_value && mapping.field_mapping);
    },
    enabled: exposedValueIds.length > 0 && fieldMappingIds.length > 0 && !!factoryInfo?.tenant_id,
  });

  const { data: areas = [] } = useQuery({
    queryKey: ["factory-areas", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("factory_areas")
        .select("id, name, color")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Area[];
    },
    enabled: !!selectedFactoryId,
  });

  const { data: lines = [] } = useQuery({
    queryKey: ["production-lines", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("production_lines")
        .select("id, area_id, name")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Line[];
    },
    enabled: !!selectedFactoryId,
  });

  const { data: stations = [] } = useQuery({
    queryKey: ["stations", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema("core")
        .from("stations")
        .select("id, line_id, name")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Station[];
    },
    enabled: !!selectedFactoryId,
  });

  const mappingByLocation = useMemo(() => {
    const map = new Map<string, Map<string, ExposedValueMapping>>();
    exposedValueMappings.forEach((mapping) => {
      if (!mapping.field_mapping) return;
      const locationKey = getLocationKey(mapping.field_mapping);
      const locationMap = map.get(locationKey) ?? new Map<string, ExposedValueMapping>();
      locationMap.set(mapping.exposed_value_id, mapping);
      map.set(locationKey, locationMap);
    });
    return map;
  }, [exposedValueMappings]);

  const getMappingForScope = (scope: LocationScope, exposedValueId: string) => {
    return mappingByLocation.get(getScopeKey(scope))?.get(exposedValueId) ?? null;
  };

  const getFieldMappingsForScope = (scope: LocationScope) => {
    switch (scope.type) {
      case "factory":
        return fieldMappings.filter(
          (mapping) => !mapping.area_id && !mapping.line_id && !mapping.station_id
        );
      case "area":
        return fieldMappings.filter(
          (mapping) => mapping.area_id === scope.id && !mapping.line_id && !mapping.station_id
        );
      case "line":
        return fieldMappings.filter(
          (mapping) => mapping.line_id === scope.id && !mapping.station_id
        );
      case "station":
        return fieldMappings.filter((mapping) => mapping.station_id === scope.id);
      default:
        return [];
    }
  };

  const mappedFieldMappingIds = useMemo(() => {
    return Array.from(new Set(exposedValueMappings.map((mapping) => mapping.field_mapping_id)));
  }, [exposedValueMappings]);

  const fetchRealTimeValues = async () => {
    if (mappedFieldMappingIds.length === 0) return;
    setIsLoadingValues(true);
    try {
      const data = await fetchCurrentValues(mappedFieldMappingIds);
      setRealTimeValues(data);
    } catch (error) {
      console.error("Error fetching real-time values:", error);
    } finally {
      setIsLoadingValues(false);
    }
  };

  useEffect(() => {
    if (mappedFieldMappingIds.length === 0) {
      setRealTimeValues({});
      return;
    }

    fetchRealTimeValues();
    const interval = setInterval(fetchRealTimeValues, 10000);
    return () => clearInterval(interval);
  }, [mappedFieldMappingIds]);

  const handleRefresh = () => {
    refetchMappings();
    refetchExposedValueMappings();
    fetchRealTimeValues();
  };

  const totalMappings = exposedValueMappings.length;
  const areaCount = new Set(
    Array.from(mappingByLocation.keys()).filter((key) => key.startsWith("area:"))
  ).size;
  const lineCount = new Set(
    Array.from(mappingByLocation.keys()).filter((key) => key.startsWith("line:"))
  ).size;
  const stationCount = new Set(
    Array.from(mappingByLocation.keys()).filter((key) => key.startsWith("station:"))
  ).size;

  const overviewDefinitions = useMemo(() => {
    return overviewExposedValueNames
      .map((name) => exposedValues.find((definition) => definition.name === name))
      .filter(Boolean) as ExposedValueDefinition[];
  }, [exposedValues]);

  const highlightDefinitions = useMemo(() => {
    return areaHighlightExposedValues
      .map((name) => exposedValues.find((definition) => definition.name === name))
      .filter(Boolean) as ExposedValueDefinition[];
  }, [exposedValues]);

  const exposedValueGroups = useMemo(() => {
    const groupConfigs = [
      {
        id: "oee",
        title: t(`${translationPrefix}.group.oee.title`, "OEE Core"),
        description: t(
          `${translationPrefix}.group.oee.description`,
          "Overall equipment effectiveness and its main components."
        ),
        icon: Gauge,
        names: performanceExposedValueNameGroups.oee,
      },
      {
        id: "output",
        title: t(`${translationPrefix}.group.output.title`, "Output"),
        description: t(
          `${translationPrefix}.group.output.description`,
          "Throughput, counts, and cycle time signals."
        ),
        icon: Activity,
        names: performanceExposedValueNameGroups.output,
      },
      {
        id: "losses",
        title: t(`${translationPrefix}.group.losses.title`, "Losses"),
        description: t(
          `${translationPrefix}.group.losses.description`,
          "Scrap and downtime indicators for the selected level."
        ),
        icon: AlertCircle,
        names: performanceExposedValueNameGroups.losses,
      },
    ];

    const mappedGroups = groupConfigs.map((group) => ({
      id: group.id,
      title: group.title,
      description: group.description,
      icon: group.icon,
      definitions: group.names
        .map((name) => exposedValues.find((definition) => definition.name === name))
        .filter(Boolean) as ExposedValueDefinition[],
    }));

    const otherDefinitions = exposedValues.filter(
      (definition) => !specialExposedValueNames.has(definition.name)
    );

    const combinedGroups = otherDefinitions.length
      ? [
          ...mappedGroups,
          {
            id: "other",
            title: t(`${translationPrefix}.group.other.title`, "Additional Signals"),
            description: t(
              `${translationPrefix}.group.other.description`,
              "Other exposed values registered for this module."
            ),
            icon: Layers,
            definitions: otherDefinitions,
          },
        ]
      : mappedGroups;

    return combinedGroups.filter((group) => group.definitions.length > 0);
  }, [exposedValues, t]);

  const scopedSelectorMappings = useMemo(() => {
    if (!selectorContext) return [];
    return getFieldMappingsForScope(selectorContext.scope);
  }, [selectorContext, fieldMappings]);

  const scopeOptions = useMemo(() => {
    if (!selectedFactoryId) return [];
    const options: LocationScope[] = [
      {
        type: "factory",
        id: selectedFactoryId,
        label: t(`${translationPrefix}.level.factory`, "Factory"),
      },
      ...areas.map((area) => ({
        type: "area" as const,
        id: area.id,
        label: `${t(`${translationPrefix}.level.area`, "Area")} • ${area.name}`,
      })),
      ...lines.map((line) => ({
        type: "line" as const,
        id: line.id,
        label: `${t(`${translationPrefix}.level.line`, "Line")} • ${line.name}`,
      })),
      ...stations.map((station) => ({
        type: "station" as const,
        id: station.id,
        label: `${t(`${translationPrefix}.level.station`, "Station")} • ${station.name}`,
      })),
    ];
    return options;
  }, [areas, lines, selectedFactoryId, stations, t]);

  useEffect(() => {
    if (!selectedFactoryId) return;
    setFocusedScopeKey((current) => current || `factory:${selectedFactoryId}`);
  }, [selectedFactoryId]);

  useEffect(() => {
    if (scopeOptions.length === 0) return;
    const exists = scopeOptions.some((scope) => getScopeKey(scope) === focusedScopeKey);
    if (!exists) {
      setFocusedScopeKey(getScopeKey(scopeOptions[0]));
    }
  }, [scopeOptions, focusedScopeKey]);

  const focusedScope = useMemo(() => {
    if (scopeOptions.length === 0) return null;
    return scopeOptions.find((scope) => getScopeKey(scope) === focusedScopeKey) ?? scopeOptions[0];
  }, [focusedScopeKey, scopeOptions]);

  const scopeOptionsByType = useMemo(() => {
    return {
      factory: scopeOptions.filter((scope) => scope.type === "factory"),
      areas: scopeOptions.filter((scope) => scope.type === "area"),
      lines: scopeOptions.filter((scope) => scope.type === "line"),
      stations: scopeOptions.filter((scope) => scope.type === "station"),
    };
  }, [scopeOptions]);

  const factoryScope: LocationScope | null = selectedFactoryId
    ? { type: "factory", id: selectedFactoryId, label: t(`${translationPrefix}.level.factory`, "Factory") }
    : null;

  const isLoading = loadingMappings || loadingExposedValues;

  if (!selectedFactoryId || !factoryScope) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Card className="p-8 text-center">
          <Factory className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">
            {t(`${translationPrefix}.empty.factory.title`, "No Factory Selected")}
          </h2>
          <p className="text-muted-foreground">
            {t(
              `${translationPrefix}.empty.factory.description`,
              "Please select a factory to view performance data."
            )}
          </p>
        </Card>
      </div>
    );
  }

  const canEditMappings =
    profile?.is_super_admin ||
    userFactories.some((factory) =>
      factory.factory_id === selectedFactoryId ? factory.role === "supervisor" : false
    );

  const handleSelectMapping = (scope: LocationScope, definition: ExposedValueDefinition) => {
    if (!canEditMappings) {
      toast.error(
        t(`${translationPrefix}.toast.permission`, "You do not have access to edit mappings")
      );
      return;
    }
    setSelectorContext({ scope, exposedValue: definition });
    setSelectorOpen(true);
  };

  const handleSelectDatapoint = async (mapping: DatapointSelection) => {
    if (!selectorContext || !factoryInfo) return;
    setIsSavingMapping(true);
    try {
      const existingMapping = getMappingForScope(
        selectorContext.scope,
        selectorContext.exposedValue.id
      );
      if (existingMapping) {
        const { error: deleteError } = await supabase
          .schema("core")
          .from("datapoint_exposed_value_mapping")
          .delete()
          .eq("id", existingMapping.id);
        if (deleteError) throw deleteError;
      }

      const { error } = await supabase
        .schema("core")
        .from("datapoint_exposed_value_mapping")
        .insert({
          tenant_id: factoryInfo.tenant_id,
          field_mapping_id: mapping.id,
          exposed_value_id: selectorContext.exposedValue.id,
        });
      if (error) throw error;

      toast.success(t(`${translationPrefix}.toast.mapped`, "Exposed value mapped"));
      queryClient.invalidateQueries({
        queryKey: ["core", "exposed-value-mappings", selectedFactoryId],
      });
      setSelectorOpen(false);
    } catch (error) {
      toast.error(t(`${translationPrefix}.toast.failed`, "Failed to map exposed value"));
      console.error(error);
    } finally {
      setIsSavingMapping(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Gauge className="h-8 w-8 text-primary" />
            {t(`${translationPrefix}.title`, "Performance Management")}
            <ModuleStatsPopover
              totalMappings={totalMappings}
              areaCount={areaCount}
              lineCount={lineCount}
              stationCount={stationCount}
              isLoading={isLoading}
              iconColorClass="text-primary"
            />
          </h1>
          <p className="text-muted-foreground">
            {t(
              `${translationPrefix}.subtitle`,
              "Monitor OEE KPIs and drill into production performance."
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isLoadingValues && (
            <Badge variant="secondary" className="gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t(`${translationPrefix}.updating`, "Updating...")}
            </Badge>
          )}
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingValues ? "animate-spin" : ""}`} />
            {t(`${translationPrefix}.refresh`, "Refresh")}
          </Button>
          <Button variant="outline" onClick={() => setSettingsOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            {t(`${translationPrefix}.settings`, "Settings")}
          </Button>
          <Link to="/admin/uns-mapper">
            <Button variant="outline">
              <ClipboardCheck className="h-4 w-4 mr-2" />
              {t(`${translationPrefix}.unsMapper`, "UNS Mapper")}
            </Button>
          </Link>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <Gauge className="h-4 w-4" />
            {t(`${translationPrefix}.tabs.overview`, "Overview")}
          </TabsTrigger>
          <TabsTrigger value="areas" className="gap-2">
            <Layers className="h-4 w-4" />
            {t(`${translationPrefix}.tabs.areas`, "By Area")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : exposedValues.length === 0 ? (
            <Card className="p-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h2 className="text-xl font-semibold mb-2">
                {t(`${translationPrefix}.empty.exposed.title`, "No exposed values found")}
              </h2>
              <p className="text-muted-foreground">
                {t(
                  `${translationPrefix}.empty.exposed.description`,
                  "Ensure the performance exposed values are registered."
                )}
              </p>
            </Card>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gauge className="h-5 w-5 text-primary" />
                    {t(`${translationPrefix}.kpis.title`, "Factory KPIs")}
                  </CardTitle>
                  <CardDescription>
                    {t(`${translationPrefix}.kpis.description`, "Snapshot of key performance metrics.")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {overviewDefinitions.map((definition) => (
                      <ExposedValueMetricCard
                        key={definition.id}
                        translationPrefix={translationPrefix}
                        definition={definition}
                        mapping={getMappingForScope(factoryScope, definition.id)}
                        realTimeValues={realTimeValues}
                        onSelect={() => handleSelectMapping(factoryScope, definition)}
                        onOpenChart={(mapping) => {
                          setSelectedMapping(mapping);
                          setChartDialogOpen(true);
                        }}
                        disabled={getFieldMappingsForScope(factoryScope).length === 0}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Factory className="h-5 w-5 text-primary" />
                    {t(`${translationPrefix}.areaHighlights.title`, "Area Highlights")}
                  </CardTitle>
                  <CardDescription>
                    {t(
                      `${translationPrefix}.areaHighlights.description`,
                      "Quick view of OEE and performance by area."
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {areas.map((area) => {
                      const scope: LocationScope = { type: "area", id: area.id, label: area.name };
                      return (
                        <Card key={area.id} className="border-border/60">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: area.color }}
                              />
                              {area.name}
                            </CardTitle>
                            <CardDescription>
                              {highlightDefinitions.length} {t(`${translationPrefix}.areaHighlights.tracked`, "tracked KPIs")}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-1 gap-3">
                              {highlightDefinitions.map((definition) => (
                                <ExposedValueTile
                                  key={definition.id}
                                  translationPrefix={translationPrefix}
                                  definition={definition}
                                  mapping={getMappingForScope(scope, definition.id)}
                                  realTimeValues={realTimeValues}
                                  compact
                                  onSelect={() => handleSelectMapping(scope, definition)}
                                  onOpenChart={(mapping) => {
                                    setSelectedMapping(mapping);
                                    setChartDialogOpen(true);
                                  }}
                                  disabled={getFieldMappingsForScope(scope).length === 0}
                                />
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                    {areas.length === 0 && (
                      <div className="col-span-full flex flex-col items-center justify-center py-12 text-muted-foreground">
                        <Factory className="h-12 w-12 opacity-30 mb-3" />
                        <p className="text-sm">
                          {t(`${translationPrefix}.areaHighlights.empty`, "No areas configured")}
                        </p>
                        <Link
                          to="/admin/factory-structure"
                          className="text-primary text-sm hover:underline mt-1"
                        >
                          {t(`${translationPrefix}.areaHighlights.cta`, "Set up factory structure →")}
                        </Link>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>
                    {t(`${translationPrefix}.levelOverview.title`, "Performance Signals")}
                  </CardTitle>
                  <CardDescription>
                    {t(
                      `${translationPrefix}.levelOverview.description`,
                      "Choose a hierarchy level to view every exposed value."
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Select value={focusedScopeKey} onValueChange={setFocusedScopeKey}>
                    <SelectTrigger className="max-w-sm">
                      <SelectValue
                        placeholder={t(
                          `${translationPrefix}.levelOverview.selectPlaceholder`,
                          "Select level"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {scopeOptionsByType.factory.map((scope) => (
                        <SelectItem key={getScopeKey(scope)} value={getScopeKey(scope)}>
                          {scope.label}
                        </SelectItem>
                      ))}
                      {scopeOptionsByType.areas.length > 0 && <SelectSeparator />}
                      {scopeOptionsByType.areas.map((scope) => (
                        <SelectItem key={getScopeKey(scope)} value={getScopeKey(scope)}>
                          {scope.label}
                        </SelectItem>
                      ))}
                      {scopeOptionsByType.lines.length > 0 && <SelectSeparator />}
                      {scopeOptionsByType.lines.map((scope) => (
                        <SelectItem key={getScopeKey(scope)} value={getScopeKey(scope)}>
                          {scope.label}
                        </SelectItem>
                      ))}
                      {scopeOptionsByType.stations.length > 0 && <SelectSeparator />}
                      {scopeOptionsByType.stations.map((scope) => (
                        <SelectItem key={getScopeKey(scope)} value={getScopeKey(scope)}>
                          {scope.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {focusedScope ? (
                    <div className="space-y-6">
                      {exposedValueGroups.map((group) => (
                        <Card key={group.id}>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <group.icon className="h-5 w-5 text-primary" />
                              {group.title}
                            </CardTitle>
                            <CardDescription>{group.description}</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <ExposedValueGrid
                              translationPrefix={translationPrefix}
                              definitions={group.definitions}
                              scope={focusedScope}
                              getMapping={getMappingForScope}
                              realTimeValues={realTimeValues}
                              onSelect={(definition) => handleSelectMapping(focusedScope, definition)}
                              onOpenChart={(mapping) => {
                                setSelectedMapping(mapping);
                                setChartDialogOpen(true);
                              }}
                              isSelectionDisabled={
                                getFieldMappingsForScope(focusedScope).length === 0
                              }
                            />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t(`${translationPrefix}.levelOverview.selectPlaceholder`, "Select level")}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="areas" className="mt-6">
          <ScrollArea className="h-[700px]">
            <div className="space-y-6">
              {areas.length === 0 ? (
                <Card className="p-8 text-center">
                  <Layers className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h2 className="text-xl font-semibold mb-2">
                    {t(`${translationPrefix}.areas.empty.title`, "No Areas Configured")}
                  </h2>
                  <p className="text-muted-foreground mb-4">
                    {t(
                      `${translationPrefix}.areas.empty.description`,
                      "Set up your factory structure to organize performance metrics by area."
                    )}
                  </p>
                  <Link to="/admin/factory-structure">
                    <Button>{t(`${translationPrefix}.areas.empty.cta`, "Configure Factory Structure")}</Button>
                  </Link>
                </Card>
              ) : (
                areas.map((area) => {
                  const areaScope: LocationScope = { type: "area", id: area.id, label: area.name };
                  const areaLines = lines.filter((line) => line.area_id === area.id);
                  return (
                    <Card key={area.id}>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: area.color }}
                          />
                          {area.name}
                          <Badge variant="secondary">{areaLines.length} {t(`${translationPrefix}.areas.lines`, "lines")}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-3">
                            {t(`${translationPrefix}.areas.values`, "Area Values")}
                          </p>
                          <ExposedValueGrid
                            translationPrefix={translationPrefix}
                            definitions={exposedValues}
                            scope={areaScope}
                            getMapping={getMappingForScope}
                            realTimeValues={realTimeValues}
                            onSelect={(definition) => handleSelectMapping(areaScope, definition)}
                            onOpenChart={(mapping) => {
                              setSelectedMapping(mapping);
                              setChartDialogOpen(true);
                            }}
                            isSelectionDisabled={getFieldMappingsForScope(areaScope).length === 0}
                          />
                        </div>

                        {areaLines.map((line) => {
                          const lineScope: LocationScope = { type: "line", id: line.id, label: line.name };
                          const lineStations = stations.filter((station) => station.line_id === line.id);

                          return (
                            <div key={line.id} className="border rounded-lg p-4 space-y-4">
                              <div className="flex items-center gap-2">
                                <GitBranch className="h-4 w-4 text-chart-2" />
                                <span className="font-medium">{line.name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {lineStations.length} {t(`${translationPrefix}.areas.stations`, "stations")}
                                </Badge>
                              </div>

                              <ExposedValueGrid
                                translationPrefix={translationPrefix}
                                definitions={exposedValues}
                                scope={lineScope}
                                getMapping={getMappingForScope}
                                realTimeValues={realTimeValues}
                                onSelect={(definition) => handleSelectMapping(lineScope, definition)}
                                onOpenChart={(mapping) => {
                                  setSelectedMapping(mapping);
                                  setChartDialogOpen(true);
                                }}
                                isSelectionDisabled={getFieldMappingsForScope(lineScope).length === 0}
                              />

                              {lineStations.map((station) => {
                                const stationScope: LocationScope = {
                                  type: "station",
                                  id: station.id,
                                  label: station.name,
                                };

                                return (
                                  <div
                                    key={station.id}
                                    className="border rounded-lg p-4 ml-4 space-y-3"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Cpu className="h-4 w-4 text-success" />
                                      <span className="text-sm font-medium">{station.name}</span>
                                    </div>
                                    <ExposedValueGrid
                                      translationPrefix={translationPrefix}
                                      definitions={exposedValues}
                                      scope={stationScope}
                                      getMapping={getMappingForScope}
                                      realTimeValues={realTimeValues}
                                      onSelect={(definition) => handleSelectMapping(stationScope, definition)}
                                      onOpenChart={(mapping) => {
                                        setSelectedMapping(mapping);
                                        setChartDialogOpen(true);
                                      }}
                                      isSelectionDisabled={
                                        getFieldMappingsForScope(stationScope).length === 0
                                      }
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {selectedMapping && (
        <MetricChartDialog
          open={chartDialogOpen}
          onOpenChange={setChartDialogOpen}
          mappingId={selectedMapping.field_mapping_id}
          displayName={
            selectedMapping.exposed_value?.display_name ||
            selectedMapping.field_mapping?.datapoint?.display_name ||
            t(`${translationPrefix}.metric.unknown`, "Unknown")
          }
          unit={selectedMapping.field_mapping?.datapoint?.unit?.symbol || null}
          currentValue={realTimeValues[selectedMapping.field_mapping_id]?.value}
          translationPrefix={`${translationPrefix}.chart`}
        />
      )}

      <ExposedValueSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        translationPrefix={translationPrefix}
        titleFallback="Performance Settings"
        descriptionFallback="Understand exposed values and manage mapping coverage across the factory."
        exposedValues={exposedValues}
        factoryScope={factoryScope}
        areas={areas}
        lines={lines}
        stations={stations}
        hasDatapoints={fieldMappings.length > 0}
        realTimeValues={realTimeValues}
        getMappingForScope={getMappingForScope}
        onSelectMapping={handleSelectMapping}
        onOpenChart={(mapping) => {
          setSelectedMapping(mapping);
          setChartDialogOpen(true);
        }}
      />

      <DatapointSelectorDialog
        open={selectorOpen}
        onOpenChange={(open) => {
          setSelectorOpen(open);
          if (!open) setSelectorContext(null);
        }}
        scopedMappings={scopedSelectorMappings}
        allMappings={fieldMappings}
        selectedId={selectorContext
          ? getMappingForScope(selectorContext.scope, selectorContext.exposedValue.id)?.field_mapping_id ?? null
          : null}
        onSelect={handleSelectDatapoint}
        title={
          selectorContext
            ? t(
                `${translationPrefix}.selector.title`,
                `Select datapoint for ${selectorContext.exposedValue.display_name}`
              )
            : t(`${translationPrefix}.selector.titleFallback`, "Select datapoint")
        }
        description={
          selectorContext
            ? t(
                `${translationPrefix}.selector.description`,
                `Assign a datapoint at ${selectorContext.scope.label} level.`
              )
            : t(
                `${translationPrefix}.selector.descriptionFallback`,
                "Choose an existing datapoint mapping."
              )
        }
      />

      {isSavingMapping && (
        <div className="fixed bottom-6 right-6">
          <Badge variant="secondary" className="gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t(`${translationPrefix}.saving`, "Saving mapping...")}
          </Badge>
        </div>
      )}
    </div>
  );
};

export default PerformanceManagement;
